// SPDX-FileCopyrightText: © 2024 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from '@stencil/core'
import { Component, h, Prop } from '@stencil/core'

import type { Importance, Size } from '../shared'

export type BadgeCounterImportance =
  | Importance
  | 'destructive'
  | 'subdued-destructive'
export type BadgeCounterSize = Size | 'hero'

/**
 * The `<qds-badge-counter>` element is a small, rounded UI element used to
 * display numerical values or counts. It is designed to draw the user's eye to
 * important or dynamic content by providing a visual cue that a countable
 * event or item has occurred or is waiting to be addressed.
 */
@Component({
  tag: 'qds-badge-counter',
  shadow: true,
  styleUrl: 'badge-counter.css',
})
export class BadgeCounter implements ComponentInterface {
  /**
   * Adds accessible text to the qds-badge-counter that will be used by screen
   * readers.
   *
   * @example
   * <qds-badge-counter value={2} description="new notifications"></qds-badge-counter>
   * // This qds-badge-counter will be read by screen readers as "two new notifications"
   */
  @Prop() public readonly description?: string

  /**
   * The badge counter's importance.
   */
  @Prop() public readonly importance?: BadgeCounterImportance = 'standard'

  /**
   * The badge counter's size.
   */
  @Prop() public readonly size?: BadgeCounterSize = 'standard'

  /**
   * Shows a stroke ring around the component for better visibility
   */
  @Prop() public readonly strokeRing: boolean = false

  /**
   * The badge-counter's value.
   */
  @Prop() public readonly value!: number | string

  get #computedValue(): string {
    const value =
      typeof this.value === 'string'
        ? Number.parseInt(this.value, 10)
        : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          (this.value ?? Number.NaN)
    if (Number.isNaN(value) || value < 0) return ''

    return value < 100 ? value.toString() : '99+'
  }

  get #computedImportance(): BadgeCounterImportance {
    switch (this.importance) {
      case 'subdued':
      case 'destructive':
      case 'subdued-destructive':
      case 'emphasized': {
        return this.importance
      }
      default: {
        return 'standard'
      }
    }
  }

  get #computedSize(): BadgeCounterSize {
    switch (this.size) {
      case 'hero':
      case 'large':
      case 'small': {
        return this.size
      }
      default: {
        return 'standard'
      }
    }
  }

  public render() {
    return (
      <span
        class={{
          'qds-badge-counter': true,
          'qds-stroke-ring': this.strokeRing,
        }}
        data-importance={this.#computedImportance}
        data-size={this.#computedSize}
      >
        {this.#computedValue}
        <span class="qds-sr-only">{this.description}</span>
      </span>
    )
  }
}
